import { useTitle } from "@/hooks/use-title.hook.ts";
import { WorkflowEditSidebar } from "@/pages/workflows/edit/components/WorkflowEditSidebar.tsx";

import { WorkflowEditBlockConditionModal } from "@/pages/workflows/edit/actions/WorkflowEditBlockConditionModal.tsx";
import { resolveTriggerConditionMode } from "@/pages/workflows/edit/actions/workflow-edit-block-condition-modal.tsx";
import { WorkflowAddBlockMenuItems } from "@/pages/workflows/edit/flow/WorkflowAddBlockMenuItems.tsx";
import { WorkflowBlockTreeRenderer } from "@/pages/workflows/edit/flow/WorkflowBlockTreeRenderer.tsx";
import { WorkflowTriggerConditionBlock } from "@/pages/workflows/edit/flow/WorkflowTriggerConditionBlock.tsx";
import {
  hasAutomationConfigurationPage,
  useWorkflowEditPage,
  WorkflowEditPageProps,
  WorkflowEditPageStep,
} from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { WorkflowTitleSlot } from "@/pages/workflows/edit/pages/WorkflowTitleSlot.tsx";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowUpdateTriggerConditions } from "@/queries/workflows/workflow.query.ts";
import { WorkflowTreeBuilder } from "@/types/workflows/workflow-tree-builder.ts";
import {
  ApiWorkflowBlockTypes,
  ApiWorkflowEventTypes,
  ApiWorkflowTriggerType,
  ApiWorkflowTriggerScheduleEntityType,
  getApiWorkflowTriggerTypeTitle,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowCondition } from "@/types/workflows/workflow.condition.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Title,
  Text,
  useModals,
  FullScreenPage,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const WorkflowEditFlowPage = ({ workflow, workflowVersion }: WorkflowEditPageProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const { confirmExit, navigateToStep } = useWorkflowEditPage();
  const { getWorkflowTriggerTypeIcon, getWorkflowTriggerDescription } = useWorkflows();
  const updateTriggerConditions = useWorkflowUpdateTriggerConditions();

  useTitle(t("workflow:configure_actions"), t("workflow:workflows"));

  const tree = useMemo(() => {
    if (!workflowVersion) return;
    const builder = new WorkflowTreeBuilder(workflowVersion.blocks, workflowVersion.edges);
    return builder.buildTree();
  }, [workflowVersion]);

  const startBlock = useMemo(() => {
    return workflowVersion?.blocks.find((block) => block.block_type == ApiWorkflowBlockTypes.start);
  }, [workflowVersion]);

  const isTimeOffTrigger =
    (workflow.trigger_type == ApiWorkflowTriggerType.scheduled &&
      workflowVersion.trigger_schedule_rule?.entity_type ==
        ApiWorkflowTriggerScheduleEntityType.time_off) ||
    workflowVersion.trigger_event?.event_type == ApiWorkflowEventTypes.time_off_request_approved;

  const saveTriggerConditions = async (conditions: ApiWorkflowCondition[]) => {
    try {
      await updateTriggerConditions.mutateAsync({
        workflowId: workflow.id,
        workflowVersionId: workflow.current_version_id,
        payload: conditions.map((condition) => ({
          entity_type: condition.entity_type,
          field_name: condition.field_name,
          operator: condition.operator,
          field_value: condition.field_value,
          condition_group: condition.condition_group,
        })),
      });
    } catch {}
  };

  const isCandidateSubject = isCandidateWorkflowEventType(
    workflowVersion.trigger_event?.event_type
  );

  const conditionModalProps = {
    mode: resolveTriggerConditionMode(isCandidateSubject, isTimeOffTrigger),
  };

  const handleAddCondition = () => {
    if (updateTriggerConditions.isPending) return;

    openModal(WorkflowEditBlockConditionModal, {
      ...conditionModalProps,
      onSave: (newCondition) => {
        void saveTriggerConditions([...workflowVersion.trigger_conditions, newCondition]);
      },
    });
  };

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit()}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={
        <WorkflowTitleSlot
          step={WorkflowEditPageStep.flow}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
      isCenterVertical={false}
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button
            onClick={() => {
              if (
                hasAutomationConfigurationPage(
                  workflow.trigger_type,
                  workflowVersion.trigger_schedule_rule?.entity_type
                )
              ) {
                navigateToStep(workflow.id, WorkflowEditPageStep.automation_setup);
              } else {
                navigateToStep(workflow.id, WorkflowEditPageStep.automation);
              }
            }}
            variant="secondary"
            size={"lg"}
          >
            {t("common:back")}
          </Button>
          <Button
            size={"lg"}
            onClick={() => {
              navigateToStep(workflow.id, WorkflowEditPageStep.final);
            }}
          >
            {t("common:continue")}
          </Button>
        </Stack>
      }
      sidebarSlot={
        <WorkflowEditSidebar
          step={WorkflowEditPageStep.flow}
          workflow={workflow}
          workflowVersion={workflowVersion}
        />
      }
    >
      <Title header={t("workflow:when")} size={"lg"} paddingBottom={true} paddingTop={true} />
      <Box variant={"whiteBorder"} className={"py-4"}>
        <ListItem
          icon={getWorkflowTriggerTypeIcon(workflow.trigger_type)}
          behavior={"card"}
          preTitle={getApiWorkflowTriggerTypeTitle(workflow.trigger_type)}
          title={getWorkflowTriggerDescription(workflowVersion)}
        />
      </Box>
      <Title header={t("workflow:if")} size={"lg"} paddingBottom={true} paddingTop={true} />
      {workflowVersion.trigger_conditions.length > 0 ? (
        <Stack gap={"sm"}>
          {workflowVersion.trigger_conditions.map((condition, conditionIndex) => (
            <Box variant={"whiteBorder"} className={"py-4"} key={condition.id}>
              <WorkflowTriggerConditionBlock
                condition={condition}
                onEdit={() => {
                  openModal(WorkflowEditBlockConditionModal, {
                    condition,
                    ...conditionModalProps,
                    onSave: (updatedCondition) => {
                      const nextConditions = workflowVersion.trigger_conditions.map(
                        (current, index) => (index === conditionIndex ? updatedCondition : current)
                      );
                      void saveTriggerConditions(nextConditions);
                    },
                  });
                }}
                onDelete={() => {
                  const nextConditions = workflowVersion.trigger_conditions.filter(
                    (_, index) => index !== conditionIndex
                  );
                  void saveTriggerConditions(nextConditions);
                }}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Box variant={"whiteBorder"} className={"py-4"}>
          <Stack className={"items-center text-center"} gap={"sm"}>
            <Text className="text-secondary">{t("workflow:conditions_empty")}</Text>
            <Button
              variant={"secondary"}
              onClick={handleAddCondition}
              disabled={updateTriggerConditions.isPending}
              isLoading={updateTriggerConditions.isPending}
            >
              {t("common:add")}
            </Button>
          </Stack>
        </Box>
      )}
      {workflowVersion.trigger_conditions.length > 0 && (
        <Box className={"py-(--gap-lg)"}>
          <Button
            variant={"secondary"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
            onClick={handleAddCondition}
            disabled={updateTriggerConditions.isPending}
            isLoading={updateTriggerConditions.isPending}
          >
            {`${t("workflow:add_condition")}...`}
          </Button>
        </Box>
      )}
      <Title header={t("workflow:run_flow")} size={"lg"} paddingBottom={true} paddingTop={true} />
      {workflowVersion && tree && (
        <>
          <WorkflowBlockTreeRenderer node={tree} workflowVersion={workflowVersion} />
        </>
      )}
      <Box className={"py-(--gap-lg)"}>
        {workflowVersion && startBlock && (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              >
                {`${t("core:add_action")}...`}
              </Button>
            }
          >
            <WorkflowAddBlockMenuItems
              workflowId={workflow.id}
              workflowVersion={workflowVersion}
              fromBlockId={startBlock.id}
            />
          </DropdownMenu>
        )}
      </Box>
    </FullScreenPage>
  );
};
